import {HttpEvent, HttpEventType, HttpUploadProgressEvent} from '@angular/common/http';
import {Injectable, PLATFORM_ID, inject} from '@angular/core';
import type {RequestBodyFileUpload} from '@app/middleware/models';
import {FileResponse} from '@app/middleware/models/file-response';
import {ResourceService} from '@app/middleware/services/resource/resource.service';
import {FileAttachment} from '@app/shared/types/file-attachment';
import {EMPTY, Observable} from 'rxjs';
import {catchError, filter, map} from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class FileService {
  private readonly resourceService = inject(ResourceService);
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * Uploads a file attachment directly without checksum checking or duplicate checks.
   *
   * @param resourceId The ID of the resource to attach the file to.
   * @param existingFiles A list of files currently attached to the resource.
   * @param newFile The new file to be uploaded.
   * @returns An Observable that emits HttpEvents (progress) or the final FileResponse.
   */
  uploadResourceAttachment(
    resourceId: string,
    existingFiles: FileAttachment[],
    newFile: File,
  ): Observable<FileResponse | HttpUploadProgressEvent> {
    const file = newFile;

    const requestBody: RequestBodyFileUpload = {
      file: file,
    };

    return this.resourceService
      .uploadResourceFile(resourceId, requestBody, {
        observe: 'events',
        reportProgress: true,
      })
      .pipe(
        map((event: HttpEvent<FileResponse>) => {
          if (event.type === HttpEventType.UploadProgress) {
            return event as HttpUploadProgressEvent;
          }
          if (event.type === HttpEventType.Response) {
            return event.body as FileResponse;
          }
          // For all other HttpEvents (Sent, Headers, etc.), return null to be filtered out.
          return null as unknown as FileResponse | HttpUploadProgressEvent | null;
        }),
        // Filter out null results for irrelevant HttpEvents (Sent, Headers, etc.)
        filter((event): event is FileResponse | HttpUploadProgressEvent => !!event),
        catchError(err => {
          console.error('File upload failed (File path):', err);
          return EMPTY;
        }),
      );
  }

  /**
   * Deletes a file from a resource.
   * @param resourceId The ID of the resource.
   * @param fileId The ID of the file to delete.
   */
  deleteAttachment(resourceId: string, fileId: string): Observable<unknown> {
    return this.resourceService.deleteResourceFile(resourceId, fileId).pipe(
      catchError(err => {
        console.error('File deletion failed:', err);
        return EMPTY;
      }),
    );
  }
}
